import React from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { UniversalLink } from '@plone/volto/components';
import { searchContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import { sourcesOf, searchOptions } from './getAsyncData';

// Status vem da taxonomia de tags do portal — a lista do dropdown é FIXA,
// não derivada do que existe no momento (senão "Cancelado" some do filtro
// sempre que não houver processo cancelado, como acontecia na versão antiga).
const STATUS_TAGS = [
  // 19/08/2026: vocabulário oficial passou a ser "em Andamento" (decisão
  // UCOM/NERSSI). O valor antigo segue mapeado para o mesmo rótulo enquanto
  // houver conteúdo legado com a tag "Aberto".
  ['Processo Seletivo em Andamento', 'Em andamento'],
  ['Processo Seletivo Aberto', 'Em andamento'],
  ['Processo Seletivo Encerrado', 'Encerrado'],
  ['Processo Seletivo Cancelado', 'Cancelado'],
];
const STATUS_OPTIONS = [...new Set(STATUS_TAGS.map(([, label]) => label))];
const SEM_STATUS = 'Não informado';
// Ordem dos grupos na tabela: em andamento primeiro; sem status logo depois
// (costuma ser edital novo ainda sem etiqueta); encerrados e cancelados no fim.
const ORDEM_STATUS = {
  'Em andamento': 0,
  [SEM_STATUS]: 1,
  Encerrado: 2,
  Cancelado: 3,
};

const normalize = (value = '') =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/\s+/g, '-');

const formatDate = (value) => {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '-';
};

const statusOf = (item) => {
  const subjects = item.Subject || item.subjects || [];
  return (
    STATUS_TAGS.find(([subject]) => subjects.includes(subject))?.[1] ||
    SEM_STATUS
  );
};

// Número do edital: prioriza o padrão "nº N/AAAA"; sem ele, aceita "nº N"
// sem ano (editais antigos da Sede, como "Edital N° 60 – …") e, por último,
// qualquer grupo numérico composto (N/AAAA, N.AAAA…). Título sem número
// vira "-".
const numberOf = (title = '') => {
  const semZeros = (n) => n.replace(/^0+(\d)/, '$1');
  const oficial = title.match(/n[º°o.]?\s*(\d{1,4}\s*\/\s*\d{4})/i);
  if (oficial) return semZeros(oficial[1].replace(/\s+/g, ''));
  const solto = title.match(/\b(\d{1,4}\/\d{4})\b/);
  if (solto) return semZeros(solto[1]);
  const semAno = title.match(/\bn[.º°o]{1,2}\s*(\d{1,4})\b(?!\s*[./]\s*\d)/i);
  if (semAno) return semZeros(semAno[1]);
  return title.match(/\b\d+(?:[./-]\d+)+(?:\/[A-Z]+)?/i)?.[0] || '-';
};

const yearOf = (numero, item) => {
  const doNumero = numero.match(/\/(20\d{2})\b/);
  if (doNumero) return doNumero[1];
  const daData = (item.effective || item.modified || '').slice(0, 4);
  return /^20\d{2}$/.test(daData) ? daData : '-';
};

const referenceOf = (title = '') => {
  const parts = title.split(/\s+[–—-]\s+/);
  if (parts.length > 1) return parts.slice(1).join(' – ');
  return (
    title
      .replace(
        // Só remove o prefixo quando há um número de edital depois dele —
        // senão "Vagas para X" perderia o começo do próprio nome.
        /^(?:edital(?:\s+do)?|processo seletivo simplificado|processo seletivo|vagas?)\b\s*(?:n[º°o.]*)?\s*\d+(?:[./-]\d+)*\s*/i,
        '',
      )
      .trim() || title
  );
};

// "Número" ordena do mais recente pro mais antigo: ano maior primeiro,
// número maior primeiro dentro do ano.
const sortNumbersDesc = (numbers) =>
  [...numbers].sort((a, b) => {
    const [na, ya] = a.split('/').map(Number);
    const [nb, yb] = b.split('/').map(Number);
    if ((yb || 0) !== (ya || 0)) return (yb || 0) - (ya || 0);
    return (nb || 0) - (na || 0);
  });

const unique = (rows, field) =>
  [
    ...new Set(rows.map((row) => row[field]).filter((v) => v && v !== '-')),
  ].sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }));

// Dimensões editoriais por tag com prefixo (decisão de 07/08/2026):
// "Área: Medicina" → filtro Área; "Local: RR" → filtro Local (UF).
const PREFIXO_AREA = 'Área: ';
const PREFIXO_LOCAL = 'Local: ';
const comPrefixo = (item, prefixo) =>
  (item.Subject || item.subjects || [])
    .filter((s) => s.startsWith(prefixo))
    .map((s) => s.slice(prefixo.length));

const uniqueFlat = (rows, field) =>
  [...new Set(rows.flatMap((row) => row[field] || []))].sort((a, b) =>
    a.localeCompare(b, 'pt-BR'),
  );

const EMPTY_FILTERS = {
  number: '',
  year: '',
  reference: '',
  status: '',
  area: '',
  local: '',
};

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M12 5c5.5 0 9.7 5.6 9.9 5.9a2 2 0 0 1 0 2.2C21.7 13.4 17.5 19 12 19s-9.7-5.6-9.9-5.9a2 2 0 0 1 0-2.2C2.3 10.6 6.5 5 12 5zm0 2c-4.1 0-7.5 4.3-8.1 5 .6.7 4 5 8.1 5s7.5-4.3 8.1-5c-.6-.7-4-5-8.1-5zm0 1.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7zm0 2a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z" />
  </svg>
);

const ProcessSearchView = ({ data, id, block }) => {
  const blockId = id || block;
  const dispatch = useDispatch();
  const sources = sourcesOf(data);

  const sede = useSelector(
    (s) => s.search?.subrequests?.[`agsusProcessSearch_${blockId}_sede`] || {},
    shallowEqual,
  );
  const dsei = useSelector(
    (s) => s.search?.subrequests?.[`agsusProcessSearch_${blockId}_dsei`] || {},
    shallowEqual,
  );

  React.useEffect(() => {
    // SSR já traz os dados via getAsyncData; este dispatch cobre navegação
    // client-side e mudança de fonte no editor.
    sources.forEach((source) => {
      dispatch(
        searchContent(
          source.path,
          searchOptions(source),
          `agsusProcessSearch_${blockId}_${source.key}`,
        ),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, blockId, sources.map((s) => s.path).join('|')]);

  const [filters, setFilters] = React.useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = React.useState(EMPTY_FILTERS);
  const [currentPage, setCurrentPage] = React.useState(1);

  const loading = Boolean(sede.loading || dsei.loading);
  const items = React.useMemo(
    () => [...(sede.items || []), ...(dsei.items || [])],
    [sede.items, dsei.items],
  );

  const rows = React.useMemo(() => {
    const mapped = items.map((item) => {
      const number = numberOf(item.title);
      return {
        ...item,
        number,
        year: yearOf(number, item),
        reference: referenceOf(item.title),
        status: statusOf(item),
        areas: comPrefixo(item, PREFIXO_AREA),
        locais: comPrefixo(item, PREFIXO_LOCAL),
      };
    });
    // Ordem padrão: primeiro o que está em andamento, depois o resto; dentro
    // de cada grupo, a publicação mais recente primeiro. Ordenar pela última
    // modificação punha no topo editais antigos que só mudaram de pasta ou de
    // etiqueta.
    const peso = (row) => ORDEM_STATUS[row.status] ?? 1;
    const publicacao = (row) => row.effective || row.modified || '';
    return mapped.sort(
      (a, b) => peso(a) - peso(b) || publicacao(b).localeCompare(publicacao(a)),
    );
  }, [items]);

  const statusOptions = React.useMemo(() => {
    const options = [...STATUS_OPTIONS];
    if (rows.some((row) => row.status === SEM_STATUS)) options.push(SEM_STATUS);
    return options;
  }, [rows]);

  const filtered = rows.filter(
    (row) =>
      (!appliedFilters.number || row.number === appliedFilters.number) &&
      (!appliedFilters.year || row.year === appliedFilters.year) &&
      (!appliedFilters.reference ||
        row.reference === appliedFilters.reference) &&
      (!appliedFilters.status || row.status === appliedFilters.status) &&
      (!appliedFilters.area ||
        (row.areas || []).includes(appliedFilters.area)) &&
      (!appliedFilters.local ||
        (row.locais || []).includes(appliedFilters.local)),
  );

  const itemsPerPage =
    Number(data.itemsPerPage) > 0 ? Number(data.itemsPerPage) : 20;
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const page = Math.min(currentPage, totalPages);
  const visible = filtered.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  const updateFilter = (event) =>
    setFilters((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));

  return (
    <section className="ag-process-search">
      <h2>{data.title || 'Filtrar processos'}</h2>
      <p>
        {data.intro ||
          'Utilize os campos abaixo para consultar determinado processo.'}
      </p>
      <form
        className="ag-process-search__filters"
        onSubmit={(event) => {
          event.preventDefault();
          setAppliedFilters(filters);
          setCurrentPage(1);
        }}
      >
        {[
          ['number', 'Número', sortNumbersDesc(unique(rows, 'number'))],
          ['year', 'Ano', unique(rows, 'year').reverse()],
          ['reference', 'Referente', unique(rows, 'reference')],
          ...(uniqueFlat(rows, 'areas').length
            ? [['area', 'Área', uniqueFlat(rows, 'areas')]]
            : []),
          ...(uniqueFlat(rows, 'locais').length
            ? [['local', 'Local (UF)', uniqueFlat(rows, 'locais')]]
            : []),
          ['status', 'Status', statusOptions],
        ].map(([field, label, options]) => (
          <label key={field}>
            <span>{label}</span>
            <select name={field} value={filters[field]} onChange={updateFilter}>
              <option value="">Todos</option>
              {options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        ))}
        <div className="ag-process-search__actions">
          <button
            type="button"
            onClick={() => {
              setFilters(EMPTY_FILTERS);
              setAppliedFilters(EMPTY_FILTERS);
              setCurrentPage(1);
            }}
          >
            Limpar filtros
          </button>
          <button type="submit">Pesquisar</button>
        </div>
      </form>
      <div className="ag-process-search__table-wrap">
        <table>
          <thead>
            <tr>
              <th>Número</th>
              <th>Referente</th>
              <th>Status</th>
              <th>Publicação</th>
              <th>Ação</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row['@id']}>
                <td>{row.number}</td>
                <td>{row.reference}</td>
                <td>
                  <span
                    className={`ag-process-search__status ag-process-search__status--${normalize(row.status)}`}
                  />
                  {row.status}
                </td>
                <td>{formatDate(row.effective || row.modified)}</td>
                <td>
                  <UniversalLink
                    className="ag-process-search__view"
                    href={flattenToAppURL(row['@id'])}
                    aria-label={`Ver ${row.title}`}
                  >
                    <EyeIcon />
                    Ver
                  </UniversalLink>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <nav
          className="ag-process-search__pager"
          aria-label="Paginação dos processos"
        >
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setCurrentPage(page - 1)}
          >
            ‹ Anterior
          </button>
          <span>
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setCurrentPage(page + 1)}
          >
            Próxima ›
          </button>
        </nav>
      )}
      <p className="ag-process-search__result" aria-live="polite">
        {loading && rows.length === 0
          ? 'Carregando processos...'
          : `${filtered.length} ${filtered.length === 1 ? 'item encontrado' : 'itens encontrados'}`}
      </p>
    </section>
  );
};

export default ProcessSearchView;
